// Sending a user to two-factor setup in the middle of something, and bringing
// them back.
//
// With MFA_ENFORCEMENT_MODE=before_regulated_action the API refuses a regulated
// action (403 AU403MFAA) instead of the router blocking the whole app up front.
// The refusal lands wherever the user happened to be — a client form, an order
// ticket — so the redirect has to remember that place and return there after
// enrolment, or the user finishes setup and is dropped on the dashboard having
// lost their place.

/** The API's code for "this action needs a second factor you have not enrolled". */
export const MFA_ENROLLMENT_REQUIRED_FOR_ACTION = 'AU403MFAA';

const SECURITY_SETUP_PATH = '/security-setup';

/** Whether a failed request is the API refusing a regulated action for lack of MFA. */
export function isMfaRequiredForAction(err: unknown): boolean {
  if (typeof err !== 'object' || err === null || !('response' in err)) {
    return false;
  }
  const response = (
    err as {
      response?: { status?: number; data?: { detail?: { code?: unknown } } };
    }
  ).response;
  return (
    response?.status === 403 &&
    response.data?.detail?.code === MFA_ENROLLMENT_REQUIRED_FOR_ACTION
  );
}

/**
 * Accept a return path only if it is a same-origin app path.
 *
 * It arrives in a query string, so it is attacker-controlled: `//evil.example`
 * and `https://evil.example` both start the wrong way, and a link to
 * /security-setup?returnTo=//evil.example would turn a security screen into an
 * open redirect. Also refuses the setup page itself, which would loop.
 */
export function safeReturnTo(value: string | null | undefined): string | null {
  if (!value) return null;
  if (
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\')
  ) {
    return null;
  }
  if (
    value === SECURITY_SETUP_PATH ||
    value.startsWith(`${SECURITY_SETUP_PATH}?`)
  ) {
    return null;
  }
  return value;
}

/** `/security-setup`, carrying the place to come back to when there is one. */
export function securitySetupUrl(returnTo: string | null | undefined): string {
  const safe = safeReturnTo(returnTo);
  return safe
    ? `${SECURITY_SETUP_PATH}?returnTo=${encodeURIComponent(safe)}`
    : SECURITY_SETUP_PATH;
}

/** Read `returnTo` from a location search string. */
export function returnToFromSearch(search: string): string | null {
  return safeReturnTo(new URLSearchParams(search).get('returnTo'));
}

// ── The one place a refusal becomes a redirect ───────────────────────────────
//
// Every caller of the API ends here, whichever transport it used: the axios
// client (including `responseType: 'blob'` downloads, whose error body arrives
// as a Blob), and `apiFetch` for the raw-`fetch` callers that bypass axios.
// Two copies of this decision drifting apart is how a regulated action ends up
// failing silently in one corner of the app.

/**
 * Background reads that must NOT bounce the user to setup.
 *
 * With MFA_ENFORCEMENT_MODE=before_regulated_action a new user sits on the
 * empty dashboard, and the shell polls these (notification bell, calendar
 * widgets, the assistant's history). They are refused for an unenrolled
 * account, and redirecting on that would eject the user from the dashboard
 * the mode exists to let them see. Their absence is the right outcome: nothing
 * renders. Anything the user DOES — a click, a submit, a download, a question
 * to the assistant — is not on this list and redirects.
 */
const PASSIVE_READ_PREFIXES = [
  '/notifications',
  '/calendar/',
  '/soteria/history',
];

/**
 * Pages an unenrolled user lands on before doing anything: onboarding and the
 * empty dashboard. Their shell reads (clients, accounts, portfolio, ...) are
 * refused too, and bouncing to setup from them put the prompt on the landing
 * page instead of at the first regulated action. Reads fired from any other
 * page, and every write, still redirect.
 */
const LANDING_PATHS = new Set(['/', '/onboarding', '/dashboard']);

function onLandingPage(): boolean {
  if (typeof window === 'undefined') return false;
  return LANDING_PATHS.has(window.location.pathname.replace(/(?<=.)\/$/, ''));
}

/**
 * Whether a refusal should be swallowed instead of redirecting to setup.
 *
 * While the workspace is being created (`/onboarding`) NOTHING the page does is
 * the "first regulated action": the invitations it sends for an institution are
 * writes, were refused, and bounced a new user to setup straight from
 * onboarding, then back to it afterwards. Those failures surface as the
 * page's own "resend from Team" message. Elsewhere only passive reads qualify.
 */
export function isPassiveRefusal(
  method: string | undefined,
  url: string,
): boolean {
  if (
    typeof window !== 'undefined' &&
    window.location.pathname.replace(/(?<=.)\/$/, '') === '/onboarding'
  ) {
    return true;
  }
  // Sending an invitation is refused for an unenrolled owner, and the Team tab
  // answers that itself ("Secure your account to send invitations", with a link
  // to setup). Bouncing them away silently left them wondering what happened.
  if (isInvitationSend(method, url)) return true;
  return isPassiveRead(method, url);
}

function isInvitationSend(method: string | undefined, url: string): boolean {
  if ((method ?? 'GET').toUpperCase() !== 'POST') return false;
  let path = url;
  try {
    path = new URL(url, 'http://x').pathname;
  } catch {
    // keep the raw string
  }
  return /^(\/v1)?\/team\/invitations\/?$/.test(path);
}

export function isPassiveRead(method: string | undefined, url: string): boolean {
  if ((method ?? 'GET').toUpperCase() !== 'GET') return false;
  if (onLandingPage()) return true;
  let path = url;
  try {
    path = new URL(url, 'http://x').pathname;
  } catch {
    // keep the raw string
  }
  // The API is mounted under /v1 in some deployments.
  const bare = path.replace(/^\/v1(?=\/)/, '');
  return PASSIVE_READ_PREFIXES.some((p) => bare.startsWith(p));
}

/** `Blob.text()` where it exists, FileReader where it does not (jsdom). */
function readBlob(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/** Parse a refusal body that may be an object, a JSON string, or a Blob. */
async function refusalCode(data: unknown): Promise<unknown> {
  let body = data;
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    try {
      body = await readBlob(body);
    } catch {
      return undefined;
    }
  }
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      return undefined;
    }
  }
  return (body as { detail?: { code?: unknown } } | null)?.detail?.code;
}

// A page can fire a burst of refused requests; each would restart the
// navigation. One redirect per window is enough, and the window expires on its
// own so a later, separate refusal is never swallowed.
let lastRedirectAt = 0;
const REDIRECT_DEBOUNCE_MS = 3000;

/** Hard-navigate to setup, remembering where the user was. */
export function redirectToSecuritySetup(): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith(SECURITY_SETUP_PATH)) return;
  const now = Date.now();
  if (now - lastRedirectAt < REDIRECT_DEBOUNCE_MS) return;
  lastRedirectAt = now;
  const here = `${window.location.pathname}${window.location.search}`;
  window.location.assign(securitySetupUrl(here));
}

/**
 * Inspect a failed request and, if it is the MFA refusal, route to setup.
 *
 * Accepts an axios error (body may be a Blob) or a fetch `Response` (cloned,
 * so the caller can still read it). Returns whether it was the refusal.
 */
export async function handleMfaRefusal(
  failure: unknown,
  request: { method?: string; url?: string } = {},
): Promise<boolean> {
  let status: number | undefined;
  let data: unknown;
  if (typeof Response !== 'undefined' && failure instanceof Response) {
    status = failure.status;
    if (status !== 403) return false;
    data = await failure
      .clone()
      .text()
      .catch(() => undefined);
  } else if (
    typeof failure === 'object' &&
    failure !== null &&
    'response' in failure
  ) {
    const response = (failure as { response?: { status?: number; data?: unknown } })
      .response;
    status = response?.status;
    data = response?.data;
  }
  if (status !== 403) return false;
  if ((await refusalCode(data)) !== MFA_ENROLLMENT_REQUIRED_FOR_ACTION) {
    return false;
  }
  if (!isPassiveRefusal(request.method, request.url ?? '')) {
    redirectToSecuritySetup();
  }
  return true;
}
